import React from "react";
import { useMutation } from "@apollo/client";
import mutations from "Shared/mutations";
import { Grid, Typography, Box } from "@material-ui/core";
import { Option } from "react-select/src/filters";
import { successToast } from "Modules/DPToast";
import moneyToCents from "Shared/money";
import { handleValidationErrors } from "Modules/HandleValidationErrors";
import {
  CreateDisbursementInput,
  CreateDisbursementMutation,
  CreateDisbursementMutationVariables,
  DisbursementReasonCategory,
} from "generated/graphql";
import { AmplitudeProvider } from "Shared/Analytics/Amplitude";
import { AmplitudeEventType } from "Shared/Analytics/Amplitude/Types/AmplitudeEventType";

export type ReceivingAccountType = "BankAccount" | "ProviderBankAccount";
export type BankAccountOptionRadioGroup = "EXISTING" | "NEW";
export type BankAccountType = "CHECKING" | "SAVINGS";
export type CreateDisbursementFormFields = {
  providerAccountId: string;
  paymentAmount: string;
  reasonOption: Option | null;
  notes?: string;
  bankAccountOption: Option | null;
  bankAccountOptionRadioGroup: BankAccountOptionRadioGroup;
  routingNumber?: string;
  accountNumber?: string;
  confirmAccountNumber?: string;
  bankAccountType?: BankAccountType;
};

export const useCreateDisbursementForm = (
  providerAccountId: string,
  refetchQueries: () => void
) => {
  const [createDisbursementMutation] = useMutation<
    CreateDisbursementMutation,
    CreateDisbursementMutationVariables
  >(mutations.CREATE_DISBURSEMENT);

  const mapToCreateDisbursementInput = (
    formFields: CreateDisbursementFormFields
  ) => {
    const input: CreateDisbursementInput = {
      providerAccountId,
      totalAmountCents: moneyToCents(formFields.paymentAmount),
      reason: (formFields.reasonOption
        ? formFields.reasonOption.value
        : "") as DisbursementReasonCategory,
    };

    if (formFields.notes) {
      input.notes = formFields.notes;
    }

    if (formFields.bankAccountOptionRadioGroup !== "EXISTING") {
      input.accountNumber = formFields.accountNumber;
      input.routingNumber = formFields.routingNumber;
      input.checking = formFields.bankAccountType === "CHECKING";
    } else {
      input.receivingAccountId = formFields.bankAccountOption
        ? formFields.bankAccountOption.value
        : undefined;
    }

    input.receivingAccountType = formFields.bankAccountOption
      ?.data as ReceivingAccountType;

    return input;
  };

  const createDisbursement = async (
    formFields: CreateDisbursementFormFields
  ) => {
    const input = mapToCreateDisbursementInput(formFields);

    try {
      const response = await createDisbursementMutation({
        variables: { input },
      });

      refetchQueries();

      if (response?.data?.createDisbursement?.message) {
        successToast(response.data.createDisbursement.message, false);
        AmplitudeProvider.logEvent({
          type: AmplitudeEventType.CyclePaymentSuccess,
        });
      }
    } catch (err) {
      handleValidationErrors(err, "Payment not sent. ");
    }
  };

  const GetDisbursementFormRow = (
    label: string | JSX.Element,
    formField: string | JSX.Element
  ) => (
    <Grid
      item
      container
      direction={"row"}
      alignContent={"center"}
      alignItems="flex-start"
    >
      <Grid item xs={3}>
        <Box mt={1}>
          {typeof label === "string" ? (
            <Typography>{label}:</Typography>
          ) : (
            label
          )}
        </Box>
      </Grid>
      <Grid item xs={5}>
        {typeof formField === "string" ? (
          <Box mt={1}>
            <Typography>{formField}</Typography>
          </Box>
        ) : (
          formField
        )}
      </Grid>
    </Grid>
  );

  return { createDisbursement, GetDisbursementFormRow };
};
